<template>
	<v-container class="pa-2 pa-sm-3" max-width="1200">
		<v-alert v-if="store.error" type="error" variant="tonal" density="compact" class="mb-3">{{ store.error }}</v-alert>

		<v-card>
			<v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
				<SideSelector />
				<v-spacer />
				<span class="text-body-2 text-medium-emphasis">Last {{ store.hours }}h</span>
				<v-btn-toggle v-model="hoursModel" mandatory density="comfortable" @update:model-value="onHoursChange">
					<v-btn :value="6" size="x-small">6h</v-btn>
					<v-btn :value="12" size="x-small">12h</v-btn>
					<v-btn :value="24" size="x-small">24h</v-btn>
					<v-btn :value="72" size="x-small">3d</v-btn>
					<v-btn :value="168" size="x-small">7d</v-btn>
				</v-btn-toggle>
			</v-card-title>
			<v-card-text class="pa-2">
				<HistoryChart :samples="store.samples" :side="selectedSide" :height="chartHeight" />
				<div v-if="!store.loading && !store.samples.length" class="text-medium-emphasis text-center py-6">
					No samples yet — let the loop run for a few minutes.
				</div>
			</v-card-text>
		</v-card>

		<v-card class="mt-3">
			<v-card-title class="d-flex align-center">
				<v-icon icon="mdi-bed" size="small" :color="sideColor(selectedSide)" class="mr-2" />
				<span>{{ names[selectedSide] }} decisions</span>
			</v-card-title>
			<v-data-table
				v-if="!isMobile"
				:headers="decisionHeaders"
				:items="rowsBySide(selectedSide)"
				items-per-page="25"
				density="compact"
				class="elevation-0 history-table"
			>
				<template v-slot:item.when="{ item }">
					<span class="text-no-wrap">{{ item.when }}</span>
				</template>
				<template v-slot:item.source="{ item }">
					<v-chip :color="sourceColor(item.sourceRaw)" size="x-small" variant="tonal">{{ item.source }}</v-chip>
				</template>
				<template v-slot:item.change="{ item }">
					<span v-if="item.changeShown" class="text-no-wrap">
						<span class="text-medium-emphasis">{{ item.oldDisplay }}</span>
						<v-icon icon="mdi-arrow-right" size="x-small" class="mx-1" />
						<strong>{{ item.newDisplay }}</strong>
					</span>
					<span v-else-if="item.currentDisplay" class="text-no-wrap text-medium-emphasis">{{ item.currentDisplay }}</span>
					<span v-else class="text-medium-emphasis">—</span>
				</template>
				<template v-slot:item.adjust="{ item }">
					<span v-if="item.adjustShown" :class="adjustClass(item.correction)">{{ item.adjustDisplay }}</span>
					<span v-else class="text-medium-emphasis">—</span>
				</template>
			</v-data-table>

			<v-list v-else density="compact" class="py-0">
				<v-list-item
					v-for="(item, i) in rowsBySide(selectedSide)"
					:key="i"
					class="decision-item"
				>
					<div class="d-flex align-center mb-1 flex-wrap" style="gap: 6px;">
						<v-chip :color="sourceColor(item.sourceRaw)" size="x-small" variant="tonal">{{ item.source }}</v-chip>
						<span class="text-caption text-medium-emphasis">{{ item.when }}</span>
					</div>
					<div class="d-flex align-center mb-1 flex-wrap text-body-2" style="gap: 12px;">
						<span v-if="item.target !== '—'" class="text-no-wrap">
							<span class="text-medium-emphasis">Target</span> {{ item.target }}
						</span>
						<span v-if="item.adjustShown" class="text-no-wrap">
							<span class="text-medium-emphasis">Adj</span>
							<span :class="adjustClass(item.correction)">&nbsp;{{ item.adjustDisplay }}</span>
						</span>
						<span v-if="item.changeShown" class="text-no-wrap">
							<span class="text-medium-emphasis">Setpoint</span>
							{{ item.oldDisplay }}<v-icon icon="mdi-arrow-right" size="x-small" class="mx-1" />{{ item.newDisplay }}
						</span>
						<span v-else-if="item.currentDisplay" class="text-no-wrap text-medium-emphasis">
							Setpoint {{ item.currentDisplay }}
						</span>
						<span v-if="item.roomTemp !== '—'" class="text-no-wrap">
							<span class="text-medium-emphasis">Room</span> {{ item.roomTemp }}°
						</span>
					</div>
					<div class="text-caption text-medium-emphasis decision-reason">{{ item.reason }}</div>
				</v-list-item>
				<v-list-item v-if="!rowsBySide(selectedSide).length">
					<span class="text-medium-emphasis">No decisions yet.</span>
				</v-list-item>
			</v-list>
		</v-card>

		<div class="text-caption text-medium-emphasis mt-3">
			<strong>Source:</strong>
			<span class="ml-2"><v-chip color="purple" size="x-small" variant="tonal">Schedule</v-chip> schedule entry transitioned (target moved)</span>
			<span class="ml-2"><v-chip color="success" size="x-small" variant="tonal">Automation</v-chip> room-temp adjust (target same)</span>
			<span class="ml-2"><v-chip color="primary" size="x-small" variant="tonal">User</v-chip> button in this app</span>
			<span class="ml-2"><v-chip color="warning" size="x-small" variant="tonal">External</v-chip> SleepMe app or physical dial (outside this app)</span>
			<span class="ml-2"><v-chip color="error" size="x-small" variant="tonal">Error</v-chip> dock write failed (loop will retry next tick)</span>
		</div>
	</v-container>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useDisplay } from "vuetify";
import historyStore from "../stores/historyStore.js";
import configStore from "../stores/configStore.js";
import HistoryChart from "../components/HistoryChart.vue";
import SideSelector from "../components/SideSelector.vue";
import { useAllSideNames } from "../composables/useSideName.js";
import { useSelectedSide } from "../composables/useSelectedSide.js";

var HOURS_KEY = "bed.history.hours";
var DEFAULT_HOURS = 24;
var VALID_HOURS = [6, 12, 24, 72, 168];
var SIDE_COLORS = { left: "#42a5f5", right: "#ec407a" };

function loadHours() {
	try {
		var raw = localStorage.getItem(HOURS_KEY);
		var n = Number(raw);
		if (VALID_HOURS.includes(n)) { return n; }
	} catch (e) { /* ignore */ }
	return DEFAULT_HOURS;
}

var store = historyStore.data;
var hoursModel = ref(loadHours());
var selectedSide = useSelectedSide();
var display = useDisplay();
var chartHeight = computed(() => display.xs.value ? 280 : 380);
var isMobile = computed(() => display.xs.value);
var names = useAllSideNames();

onMounted(() => {
	historyStore.methods.load(hoursModel.value);
	if (!configStore.data.data) { configStore.methods.load(); }
});

function onHoursChange(v) {
	if (v) {
		try { localStorage.setItem(HOURS_KEY, String(v)); } catch (e) { /* ignore */ }
		historyStore.methods.load(v);
	}
}

function sideColor(side) { return SIDE_COLORS[side]; }

var decisionHeaders = [
	{ title: "When", key: "when" },
	{ title: "Source", key: "source" },
	{ title: "Target", key: "target" },
	{ title: "Adjust", key: "adjust" },
	{ title: "Setpoint", key: "change" },
	{ title: "Room °F", key: "roomTemp" },
	{ title: "Reason", key: "reason" },
];

function sourceLabel(s) {
	switch (s) {
	case "schedule": return "Schedule";
	case "loop": return "Automation";
	case "action": return "User";
	case "manual": return "User";
	case "external": return "External";
	case "error": return "Error";
	default: return s ?? "—";
	}
}

function sourceColor(s) {
	switch (s) {
	case "schedule": return "purple";
	case "loop": return "success";
	case "action": return "primary";
	case "manual": return "primary";
	case "external": return "warning";
	case "error": return "error";
	default: return "grey";
	}
}

function fmtSp(v) {
	return v == null ? "—" : Math.round(Number(v)) + "°";
}

function fmtAdjust(c) {
	if (c == null || c === 0) { return "0°"; }
	var n = Number(c);
	return (n > 0 ? "+" : "") + n.toFixed(1) + "°";
}

function adjustClass(c) {
	if (c == null || c === 0) { return "text-medium-emphasis"; }
	return c > 0 ? "text-warning" : "text-info";
}

function fmtWhen(ts) {
	var d = new Date(ts);
	var date = d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" });
	var time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
	return date + " " + time;
}

function sideRoomAdjustEnabled(cfg, side) {
	var rae = cfg?.roomAdjustEnabled;
	if (rae == null) { return true; }
	if (typeof rae === "boolean") { return rae; }
	return rae[side] !== false;
}

function computeCorrection(roomTemp, side, cfg) {
	if (cfg == null || roomTemp == null) { return null; }
	if (!sideRoomAdjustEnabled(cfg, side)) { return null; }
	var influence = cfg.roomTempInfluence ?? 0;
	if (influence === 0) { return null; }
	var neutral = cfg.roomNeutralTemp;
	if (neutral == null) { return null; }
	var delta = (roomTemp - neutral) * influence;
	return Number((-delta).toFixed(1));
}

function rowsBySide(side) {
	var cfg = configStore.data.data;
	return (store.decisions ?? [])
		.filter((r) => r.side === side)
		.map((r) => {
			var hasChange = r.oldSp != null && r.newSp != null && r.oldSp !== r.newSp;
			var hasCurrent = !hasChange && (r.oldSp != null || r.newSp != null);
			var current = r.newSp ?? r.oldSp;
			var roomTemp = r.inputs?.room?.temp;
			// Prefer the correction stored at decision time (accurate to that moment's
			// config). Fall back to a current-config calculation so older rows and
			// user-action rows still show a value when room temp is known.
			var correction = r.inputs?.correction;
			if (correction == null) { correction = computeCorrection(roomTemp, side, cfg); }
			// Target is recorded on "set" decisions; legacy rows used `baseTarget`.
			// For other rows, infer from setpoint − correction so the column isn't
			// empty for noop/release/etc. when we have enough info.
			var target = r.inputs?.target ?? r.inputs?.baseTarget;
			if (target == null && current != null && correction != null) {
				target = Math.round(current - correction);
			}
			return {
				when: fmtWhen(r.ts),
				sourceRaw: r.source,
				source: sourceLabel(r.source),
				changeShown: hasChange,
				oldDisplay: fmtSp(r.oldSp),
				newDisplay: fmtSp(r.newSp),
				currentDisplay: hasCurrent ? fmtSp(current) : null,
				target: target == null ? "—" : Math.round(Number(target)) + "°",
				roomTemp: roomTemp == null ? "—" : Number(roomTemp).toFixed(1),
				adjustShown: correction != null,
				correction: correction,
				adjustDisplay: fmtAdjust(correction),
				reason: r.reason,
			};
		});
}
</script>

<style scoped>
.decision-item {
	border-bottom: 1px solid rgba(255, 255, 255, 0.07);
	padding-top: 8px;
	padding-bottom: 8px;
}
.decision-item:last-child {
	border-bottom: none;
}
.decision-reason {
	white-space: normal;
	word-break: break-word;
	line-height: 1.3;
}
</style>
